import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {CoreComponent} from './modules/core/core.component';
import {DictionaryResolver} from './resolvers/dictionary.resolver';
import {AuthGuard} from './guards/auth.guard';
import {NavItemCodes} from './enums/nav-item-codes.enum';
import {ForbiddenComponent} from './modules/core/components/forbidden/forbidden.component';
import {AclGuard} from './guards/acl.guard';
import {permissionMap} from './helpers/permissions.helper';
import {UserMessagesResolver} from './resolvers/user-messages.resolver';
import {SearchListEnum} from './enums/search-list.enum';
import {DictionaryCode} from './enums/dictionary-code.enum';
import {ConfigResolver} from './resolvers/config.resolver';
import {CoreDictionaryResolver} from './resolvers/core-dictionary.resolver';

const routes: Routes = [
    {
        path: 'login',
        loadChildren: () => import('./modules/auth/auth.module').then((m) => m.AuthModule),
        data: {
            breadcrumb: null,
        },
    },
    {path: 'password-reset/:token', redirectTo: 'login/password-reset/:token', pathMatch: 'full'},
    {
        path: '',
        component: CoreComponent,
        canActivate: [AuthGuard],
        data: {
            breadcrumb: NavItemCodes.HOME,
            dictionaries: [
                DictionaryCode.PERSON_LEGAL_FORM,
                DictionaryCode.ORGANIZATION_TYPE,
                DictionaryCode.PERSON_SEX,
                DictionaryCode.IDENTITY_DOCUMENT_TYPE,
                DictionaryCode.PKD,
                DictionaryCode.CITIZENSHIP,
                DictionaryCode.GUS_BASIC_LEGAL_FORM,
                DictionaryCode.GUS_DETAILED_LEGAL_FORM,
                DictionaryCode.ADDRESS_SOURCE,
                DictionaryCode.COUNTRY,
                DictionaryCode.STREET_PREFIX,
                DictionaryCode.VOIVODESHIP,
                DictionaryCode.GUS_OWNERSHIP_FORM,
                DictionaryCode.EMAIL_NOTIFICATION,
            ],
        },
        resolve: {
            messages: UserMessagesResolver,
            dictionaries: CoreDictionaryResolver,
            gusConfig: ConfigResolver,
        },
        children: [
            {
                path: 'start',
                loadChildren: () => import('./modules/start/start.module').then((m) => m.StartModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'admin',
                loadChildren: () => import('./modules/admin/admin.module').then((m) => m.AdminModule),
                data: {
                    breadcrumb: NavItemCodes.MANAGEMENT,
                    permissionArea: permissionMap.adminPanel,
                },
                canActivate: [AclGuard],
            },
            {
                path: 'calculation',
                loadChildren: () => import('./modules/calculation/calculation.module').then((m) => m.CalculationModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'generalContract',
                loadChildren: () =>
                    import('./modules/general-contract/general-contract.module').then((m) => m.GeneralContractModule),
                data: {
                    breadcrumb: null,
                },
                // TODO canActivate: [AclGuard],
            },
            {
                path: 'policy',
                loadChildren: () => import('./modules/policy/policy.module').then((m) => m.PolicyModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'business-document',
                loadChildren: () =>
                    import('./modules/business-document/business-document.module').then(
                        (m) => m.BusinessDocumentModule
                    ),
                data: {
                    breadcrumb: NavItemCodes.BUSINESS_DOCUMENT_MANAGEMENT,
                    searchListEnum: SearchListEnum.BUSINESS_DOCUMENT,
                    adminContext: false,
                },
            },
            {
                path: 'policy-exclusions',
                loadChildren: () =>
                    import('./modules/policy-exclusions/policy-exclusions.module').then(
                        (m) => m.PolicyExclusionsModule
                    ),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'agro-exclusions',
                loadChildren: () =>
                    import('./modules/agro-exclusions/agro-exclusions.module').then((m) => m.AgroExclusionsModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'external-systems',
                loadChildren: () =>
                    import('./modules/external-systems/external-systems.module').then((m) => m.ExternalSystemsModule),
            },
            {
                path: 'annex',
                loadChildren: () => import('./modules/annex/annex.module').then((m) => m.AnnexModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'registration',
                loadChildren: () =>
                    import('./modules/registration/registration.module').then((m) => m.RegistrationModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'reports',
                loadChildren: () => import('./modules/reports/reports.module').then((m) => m.ReportsModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'special-discount',
                loadChildren: () =>
                    import('./modules/special-discount/special-discount.module').then((m) => m.SpecialDiscountModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'policy-renewals',
                loadChildren: () =>
                    import('./modules/policy-renewals/policy-renewals.module').then((m) => m.PolicyRenewalsModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'central-renewals',
                loadChildren: () =>
                    import('./modules/central-renewals/central-renewals.module').then((m) => m.CentralRenewalsModule),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'enelmed-certificate',
                loadChildren: () =>
                    import('./modules/enelmed-certificate/enelmed-certificate.module').then(
                        (m) => m.EnelmedCertificateModule
                    ),
                data: {
                    breadcrumb: null,
                },
            },
            {
                path: 'forbidden',
                component: ForbiddenComponent,
            },
            {path: '**', redirectTo: 'start'},
        ],
    },
    {path: '**', redirectTo: 'login'},
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            scrollOffset: [0, 0],
            scrollPositionRestoration: 'top',
            bindToComponentInputs: true,
        }),
    ],
    exports: [RouterModule],
    providers: [DictionaryResolver, AclGuard, UserMessagesResolver, ConfigResolver],
})
export class AppRoutingModule {}
